import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { type ShortcutKey, useKeyboardShortcut } from "./use-keyboard-shortcut";
import { useIsMobile } from "./use-mobile";

const DETAILS_PANE_STORAGE_KEY = "istari-details-pane-state";

export const DETAILS_PANE_KEYBOARD_SHORTCUT: ShortcutKey[] = ["meta", "]"];
export const DETAILS_PANE_DEFAULT_SIZE = 25;
export const DETAILS_PANE_MIN_SIZE = 15;
export const DETAILS_PANE_MAX_SIZE = 40;

function getStorageKeyName(storageKey?: string): string {
  return storageKey ? `${DETAILS_PANE_STORAGE_KEY}_${storageKey}` : DETAILS_PANE_STORAGE_KEY;
}

function getInitialOpenState(defaultOpen: boolean, storageKey?: string): boolean {
  const storedValue = localStorage.getItem(getStorageKeyName(storageKey));
  return storedValue !== null ? storedValue === "true" : defaultOpen;
}

type DetailsPaneContextProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  setIsOpenWithoutPersist: (open: boolean) => void;
  togglePane: () => void;
  open: () => void;
  isMobile: boolean;
  panelRef: React.RefObject<ImperativePanelHandle | null>;
  storageKey?: string;
  onPanelCollapse: () => void;
  onPanelExpand: () => void;
  registerToggleLock: (locked: boolean) => void;
};

type DetailsPaneActionsContextProps = {
  open: () => void;
};

const DetailsPaneContext = React.createContext<DetailsPaneContextProps | null>(null);
const DetailsPaneActionsContext = React.createContext<DetailsPaneActionsContextProps | null>(null);

export function useDetailsPane() {
  const context = useContext(DetailsPaneContext);
  if (!context) {
    throw new Error("useDetailsPane must be used within a DetailsPaneProvider.");
  }
  return context;
}

export function useDetailsPaneOptional() {
  return useContext(DetailsPaneContext);
}

export function useDetailsPaneActionsOptional() {
  return useContext(DetailsPaneActionsContext);
}

export function useDetailsPaneToggleLock(locked: boolean) {
  const { registerToggleLock } = useDetailsPane();
  useEffect(() => {
    registerToggleLock(locked);
    return () => registerToggleLock(false);
  }, [locked, registerToggleLock]);
}

interface DetailsPaneProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
}

export function DetailsPaneProvider({ children, defaultOpen = true, storageKey }: DetailsPaneProviderProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<ImperativePanelHandle>(null);

  const [isOpen, setIsOpenInternal] = useState(() => getInitialOpenState(defaultOpen, storageKey));
  const skipPersistedOpenStateEffectRef = useRef(true);
  const persistNextOpenStateRef = useRef(true);
  const toggleLockedRef = useRef(false);

  const storageKeyName = getStorageKeyName(storageKey);

  const onPanelCollapse = useCallback(() => {
    if (toggleLockedRef.current) {
      return;
    }
    persistNextOpenStateRef.current = true;
    setIsOpenInternal(false);
  }, []);

  const onPanelExpand = useCallback(() => {
    if (toggleLockedRef.current) {
      return;
    }
    persistNextOpenStateRef.current = true;
    setIsOpenInternal(true);
  }, []);

  const syncPanelOpenState = useCallback(
    (open: boolean) => {
      if (isMobile) {
        return;
      }

      const panel = panelRef.current;
      if (!panel) {
        return;
      }

      try {
        panel.getSize();
      } catch {
        // Expected while the resizable panel group is not mounted yet.
        return;
      }

      if (open) {
        if (panel.isCollapsed()) {
          panel.expand();
        }
        return;
      }

      if (!panel.isCollapsed()) {
        panel.collapse();
      }
    },
    [isMobile],
  );

  useEffect(() => {
    if (skipPersistedOpenStateEffectRef.current) {
      skipPersistedOpenStateEffectRef.current = false;
      return;
    }

    if (persistNextOpenStateRef.current) {
      localStorage.setItem(storageKeyName, String(isOpen));
    }
    persistNextOpenStateRef.current = true;
    syncPanelOpenState(isOpen);
  }, [isOpen, storageKeyName, syncPanelOpenState]);

  const setIsOpen = useCallback((open: boolean) => {
    persistNextOpenStateRef.current = true;
    setIsOpenInternal(open);
  }, []);

  const setIsOpenWithoutPersist = useCallback((open: boolean) => {
    persistNextOpenStateRef.current = false;
    setIsOpenInternal(open);
  }, []);

  const registerToggleLock = useCallback((locked: boolean) => {
    toggleLockedRef.current = locked;
  }, []);

  const togglePane = useCallback(() => {
    if (toggleLockedRef.current) {
      return;
    }
    persistNextOpenStateRef.current = true;
    setIsOpenInternal((prev) => !prev);
  }, []);

  const open = useCallback(() => {
    if (toggleLockedRef.current) {
      return;
    }
    const storedValue = localStorage.getItem(storageKeyName);
    if (storedValue === "false") {
      return;
    }
    setIsOpen(true);
  }, [storageKeyName, setIsOpen]);

  useKeyboardShortcut(DETAILS_PANE_KEYBOARD_SHORTCUT, togglePane);

  const contextValue = useMemo<DetailsPaneContextProps>(
    () => ({
      isOpen,
      setIsOpen,
      setIsOpenWithoutPersist,
      togglePane,
      open,
      isMobile,
      panelRef,
      storageKey,
      onPanelCollapse,
      onPanelExpand,
      registerToggleLock,
    }),
    [
      isOpen,
      setIsOpen,
      setIsOpenWithoutPersist,
      togglePane,
      open,
      isMobile,
      storageKey,
      onPanelCollapse,
      onPanelExpand,
      registerToggleLock,
    ],
  );

  // Rows only need the command, not the open/closed state; split the action context to avoid
  // re-rendering large file trees when the details pane toggles.
  const actionsContextValue = useMemo<DetailsPaneActionsContextProps>(
    () => ({
      open,
    }),
    [open],
  );

  return (
    <DetailsPaneActionsContext.Provider value={actionsContextValue}>
      <DetailsPaneContext.Provider value={contextValue}>{children}</DetailsPaneContext.Provider>
    </DetailsPaneActionsContext.Provider>
  );
}
